import React, { useState, useEffect } from 'react';
import axios from 'axios';
import QuestionsModule from '../Questions/Questions';

function Recommendation({
  recommendation,
  userInfo,
  answers,
  currentQuoteNumber,
  questions,
  handlePrint,
  resetQuoteGenerator,
  backToQuotesList,
  totalPrice,
  items,
  setAnswers,
  setRecommendation,
  user,
  getTotalPrice,
  createdAt,
  userDiscount,
  setUserDiscount,
  setItems,
  setTotalPrice
}) {

  const BASE_URL = process.env.NODE_ENV === 'production'
    ? (process.env.REACT_APP_IS_BETA === 'true' ? 'https://betaquotes.omntec.com' : 'https://quotes.omntec.com')
    : window.location.protocol === 'https:' 
        ? 'https://localhost:3001'
        : 'http://localhost:3001';


  const [isSiteConfigExpanded, setIsSiteConfigExpanded] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [tempAnswer, setTempAnswer] = useState('');
  const [isEditingFacilityInfo, setIsEditingFacilityInfo] = useState(false);
  const [editedFacilityInfo, setEditedFacilityInfo] = useState({
    email: userInfo.email,
    name: userInfo.name,
    company: userInfo.company,
    projectName: userInfo.projectName,
    city: userInfo.city,
    state: userInfo.state,
    phoneNumber: userInfo.phoneNumber
  });

  // Extract slotExceeded from the quote details if available
  const [slotExceeded, setSlotExceeded] = useState(false);
  
  // Add a state to store the calculated validity date
  const [validityInfo, setValidityInfo] = useState({
    date: '',
    isExpired: false
  });

  // State to track missing questions for sensors
  const [missingSensorQuestions, setMissingSensorQuestions] = useState([]);

  // Sync editedFacilityInfo when userInfo changes
  useEffect(() => {
    setEditedFacilityInfo({
      email: userInfo.email,
      name: userInfo.name,
      company: userInfo.company,
      projectName: userInfo.projectName,
      city: userInfo.city,
      state: userInfo.state,
      phoneNumber: userInfo.phoneNumber
    });
  }, [userInfo]);

  // Update validity info when createdAt changes
  useEffect(() => {
    const newValidityInfo = getValidityDate();
    setValidityInfo(newValidityInfo);
    console.log("Updated validity info:", newValidityInfo);
  }, [createdAt]);

  useEffect(() => {
    // Check if we have a quote with slotExceeded flag
    const checkSlotExceeded = async () => {
      // Reset slotExceeded state when loading a new quote
      setSlotExceeded(false);
      
      if (currentQuoteNumber) {
        try {
          const response = await axios.get(`${BASE_URL}/api/quote/${currentQuoteNumber}`);
          if (response.data.success && response.data.quote) {
            let quoteDetails;
            try {
              // Handle different formats of quote_details
              if (typeof response.data.quote.quote_details === 'string') {
                quoteDetails = JSON.parse(response.data.quote.quote_details);
              } else {
                quoteDetails = response.data.quote.quote_details;
              }
              
              // Check if slotExceeded exists in the quote details
              if (quoteDetails && quoteDetails.slotExceeded) {
                console.log("Setting slotExceeded to true from quote details");
                setSlotExceeded(true);
              }
            } catch (parseError) {
              console.error('Error parsing quote details:', parseError);
            }
          }
        } catch (error) {
          console.error('Error checking slot exceeded status:', error);
        }
      }
    };
    
    checkSlotExceeded();
  }, [currentQuoteNumber]);

  // Add a new useEffect to check for XB-800 and XB-416 boards in the items
  useEffect(() => {
    // Count the number of expansion boards in the items
    if (items && items.length > 0) {
      const xb800Items = items.filter(item => 
        item.name === 'XB-800' || item.name.includes('XB-800 (')
      );
      const xb416Items = items.filter(item => 
        item.name === 'XB-416' || item.name.includes('XB-416 (')
      );
      
      const totalBoards = xb800Items.reduce((total, item) => total + item.quantity, 0) + 
                          xb416Items.reduce((total, item) => total + item.quantity, 0);
      
      console.log("Total expansion boards:", totalBoards);
      
      // If we have more than 5 boards, set slotExceeded to true
      if (totalBoards > 5) {
        console.log("Setting slotExceeded to true based on board count");
        setSlotExceeded(true);
      } else {
        // Only reset to false if we're sure there aren't too many boards
        // This avoids overriding the slotExceeded flag from the quote details
        console.log("Board count is within limits");
      }
    }
  }, [items]);

  // Check for missing sensor-related questions
  // Only show warning if sensors are configured but key questions haven't been answered at all
  // (indicating user added sensors via editing without completing the sensor questionnaire)
  useEffect(() => {
    const missing = [];
    const totalSensors = parseInt(answers['How many Total Leak Sensors?']) || 0;
    const sumpSensors = parseInt(answers['How many Sump / Dispenser Sensors?']) || 0;
    const intersticeSensors = parseInt(answers['How many Interstice Sensors?']) || 0;

    if (totalSensors > 0) {
      // For sump sensors: check if ANY sump-related question was answered
      if (sumpSensors > 0) {
        const hasSumpAnswers = 
          answers['Is Product Distinguishing required for Sump/Dispenser Sensors?'] !== undefined ||
          answers['2-conductor field wiring for sump/dispenser sensors?'] !== undefined;
        
        if (!hasSumpAnswers) {
          missing.push('Is Product Distinguishing required for Sump/Dispenser Sensors?');
          missing.push('2-conductor field wiring for sump/dispenser sensors?');
        }
      }

      // For interstice sensors: check if ANY interstice-related question was answered
      if (intersticeSensors > 0) {
        const hasIntersticeAnswers = 
          answers['Is the interstice wet(BRINE)?'] !== undefined ||
          answers['Is Product Distinguishing required for Interstice?'] !== undefined ||
          answers['Opening for Interstice Sensor?'] !== undefined;
        
        if (!hasIntersticeAnswers) {
          missing.push('Is the interstice wet(BRINE)?');
          missing.push('Is Product Distinguishing required for Interstice?');
          missing.push('Opening for Interstice Sensor?');
        }
      }
    }

    setMissingSensorQuestions(missing);
    
    // Auto-expand Site Configuration if there are missing questions
    if (missing.length > 0) {
      setIsSiteConfigExpanded(true);
    }
  }, [answers]);

  const getValidityDate = () => {
    // Log the createdAt value for debugging
    console.log("Current createdAt value:", createdAt);
    
    if (!createdAt) {
      // If createdAt is not available, use today's date and add 30 days
      const today = new Date();
      const validityDate = new Date(today);
      validityDate.setDate(validityDate.getDate() + 30);
      
      return {
        date: validityDate.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        isExpired: false
      };
    }
    
    const validityDate = new Date(createdAt);
    validityDate.setDate(validityDate.getDate() + 30);
    const currentDate = new Date();
    
    return {
      date: validityDate.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      isExpired: currentDate > validityDate
    };
  };

  const handleSave = async (question) => {
    
    // Update answers with new value
    let newAnswers = { ...answers };
    
    // Check if this is a multi-tank question
    const isMultiTankQuestion = 
        question === 'AST OR UST?' || 
        question === 'Tank Material Type?' || 
        question === 'What is the Product / Fuel Type in each Tank?' || 
        question === 'Opening for Probe?' ||
        question === 'Inside diameter of tanks?' ||
        question === 'Probe Length' ||
        question === '2-conductor field wiring for interstice sensors?';
        
    // Check if this is a fill location question
    const isFillLocationQuestion = question === 'Remote Overfill Alarm?';
    
    if ((isMultiTankQuestion || isFillLocationQuestion) && tempAnswer && tempAnswer.startsWith('{')) {
        // For multi-tank questions and fill location questions, tempAnswer contains a JSON string of all answers
        try {
            const parsedAnswers = JSON.parse(tempAnswer);
            newAnswers = { ...newAnswers, ...parsedAnswers };
        } catch (error) {
            console.error('Error parsing answers:', error);
        }
    } else {
        // For regular questions
        newAnswers[question] = tempAnswer || answers[question];
    }
    
    setAnswers(newAnswers);

    // Recalculate initial recommendation from scratch
    const baseRecommendation = QuestionsModule.determineInitialRecommendation(newAnswers);
    
    // Recalculate all accessories with the new answers
    const finalRecommendationResult = QuestionsModule.determineFinalRecommendation(
        baseRecommendation,
        newAnswers
    );

    // Handle both old and new return formats
    let finalAccessories = [];
    let newSlotExceeded = false;
    
    if (finalRecommendationResult) {
        if (Array.isArray(finalRecommendationResult)) {
            // Old format - just an array of accessories
            finalAccessories = finalRecommendationResult;
        } else if (typeof finalRecommendationResult === 'object') {
            // New format - object with accessories and slotExceeded
            finalAccessories = finalRecommendationResult.accessories || [];
            newSlotExceeded = finalRecommendationResult.slotExceeded || false;
        }
    }
    
    // Update slotExceeded state
    setSlotExceeded(newSlotExceeded);

    // Format the final recommendation string
    const finalRecommendation = finalAccessories.length > 0 
        ? `Proteus ${baseRecommendation} with ${finalAccessories.join(', ')}`
        : `Proteus ${baseRecommendation}`;

    try {
        const priceInfo = getTotalPrice(finalRecommendation, userDiscount, newAnswers);
        
        await axios.put(`${BASE_URL}/api/update_quotes/${currentQuoteNumber}`, {
            quote_details: JSON.stringify({
                recommendation: finalRecommendation,
                answers: newAnswers,
                facilityInfo: {
                    email: userInfo.email,
                    name: userInfo.name,
                    company: userInfo.company,
                    phoneNumber: userInfo.phoneNumber,
                    projectName: userInfo.projectName,
                    city: userInfo.city,
                    state: userInfo.state
                },
                items: priceInfo.items,
                totalPrice: priceInfo.totalPrice,
                slotExceeded: newSlotExceeded
            })
        });

        // Update local state with new recommendation
        setRecommendation(finalRecommendation);
        
        // Reset editing state
        setEditingQuestion(null);
        setTempAnswer('');
    } catch (error) {
        console.error('Error updating quote:', error);
    }
  };

  // Toggle for site configuration expand/collapse
  const toggleSiteConfig = () => {
    setIsSiteConfigExpanded(!isSiteConfigExpanded);
  };

  // Handle facility info editing
  const handleEditFacilityInfo = () => {
    setEditedFacilityInfo({
      email: userInfo.email,
      name: userInfo.name,
      company: userInfo.company,
      projectName: userInfo.projectName,
      city: userInfo.city,
      state: userInfo.state,
      phoneNumber: userInfo.phoneNumber
    });
    setIsEditingFacilityInfo(true);
  };

  const handleCancelEditFacilityInfo = () => {
    setIsEditingFacilityInfo(false);
  };

  const handleFacilityInfoChange = (field, value) => {
    setEditedFacilityInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveFacilityInfo = async () => {
    try {
      // Update the quote with new facility info
      await axios.put(`${BASE_URL}/api/update_quotes/${currentQuoteNumber}`, {
        quote_details: JSON.stringify({
          recommendation: recommendation,
          answers: answers,
          facilityInfo: {
            email: editedFacilityInfo.email,
            name: editedFacilityInfo.name,
            company: editedFacilityInfo.company,
            projectName: editedFacilityInfo.projectName,
            city: editedFacilityInfo.city,
            state: editedFacilityInfo.state,
            phoneNumber: editedFacilityInfo.phoneNumber
          },
          items: items,
          totalPrice: totalPrice,
          slotExceeded: slotExceeded
        })
      });

      // Update the parent component's userInfo
      Object.assign(userInfo, editedFacilityInfo);
      
      setIsEditingFacilityInfo(false);
    } catch (error) {
      console.error('Error updating facility info:', error);
      alert('Failed to update facility information. Please try again.');
    }
  };

  // Extract model from recommendation for display
  const [modelLine] = recommendation.split(' with ');
  const model = modelLine.replace('Proteus ', '').trim();

  const handleAnswerEdit = (event, question) => {
    const value = event.target.value;
    
    setAnswers(prev => {
        const newAnswers = { ...prev, [question]: value };
        
        // Recalculate initial recommendation
        const baseRecommendation = QuestionsModule.determineInitialRecommendation(newAnswers);
        
        // Recalculate accessories with new answers
        const finalRecommendationResult = QuestionsModule.determineFinalRecommendation(baseRecommendation, newAnswers);
        
        // Handle both old and new return formats
        let finalAccessories = [];
        let newSlotExceeded = false;
        
        if (finalRecommendationResult) {
            if (Array.isArray(finalRecommendationResult)) {
                // Old format - just an array of accessories
                finalAccessories = finalRecommendationResult;
            } else if (typeof finalRecommendationResult === 'object') {
                // New format - object with accessories and slotExceeded
                finalAccessories = finalRecommendationResult.accessories || [];
                newSlotExceeded = finalRecommendationResult.slotExceeded || false;
            }
        }
        
        // Update slotExceeded state
        setSlotExceeded(newSlotExceeded);
        
        // Format final recommendation
        const finalRecommendation = finalAccessories.length > 0 
            ? `Proteus ${baseRecommendation} with ${finalAccessories.join(', ')}`
            : `Proteus ${baseRecommendation}`;
            
        // Update recommendation first
        setRecommendation(finalRecommendation);

        //Save the updated quote to the database
        try {
            // Pass finalRecommendation and newAnswers to ensure we use the updated values
            const priceInfo = getTotalPrice(finalRecommendation, userDiscount, newAnswers);
            if (priceInfo) 
            {
                setItems(priceInfo.items || []);
                setTotalPrice(priceInfo.totalPrice || 0);

                // Only update the database if we have a quote number
                if (currentQuoteNumber) 
                {
                    axios.put(`${BASE_URL}/api/update_quotes/${currentQuoteNumber}`, {
                        quote_details: JSON.stringify({
                            recommendation: finalRecommendation,
                            answers: newAnswers,
                            facilityInfo: userInfo,
                            items: priceInfo.items,
                            totalPrice: priceInfo.totalPrice,
                            slotExceeded: newSlotExceeded
                        })
                    });
                }
            }
        } catch (error) {
            console.error('Error updating quote:', error);
        }
        
        return newAnswers;
    });
  };

  const renderQuestionAnswer = (question) => {
    const isEditing = editingQuestion === question;
    const questionObj = QuestionsModule.questions.find(q => q.question === question) || 
                       QuestionsModule.initialQuestions.find(q => q.question === question);
    
    if (!questionObj) return null;

    const getOptions = () => {
        if (typeof questionObj.options === 'function') {
            return questionObj.options(answers);
        } else if (typeof questionObj.getOptions === 'function') {
            // For tank/probe count question, pass full answers to check the first initial question
            if (question === 'How many Tanks/Probes, for level monitoring?') {
                return questionObj.getOptions(answers);
            }
            // For other questions (like serial ports, sensors), pass model string
            return questionObj.getOptions(answers['Proteus Model'] || '');
        }
        return questionObj.options || [];
    };

    // Handle multi-tank questions
    const isMultiTankQuestion = 
        question === 'AST OR UST?' || 
        question === 'Tank Material Type?' || 
        question === 'What is the Product / Fuel Type in each Tank?' || 
        question === 'Opening for Probe?' ||
        question === 'Inside diameter of tanks?' ||
        question === 'Probe Length' ||
        question === 'Remote Overfill Alarm?' ||
        question === '2-conductor field wiring for interstice sensors?';
        
    // Handle fill location questions separately
    const isFillLocationQuestion = question === 'Remote Overfill Alarm?';

    if (isFillLocationQuestion) {
        const fillLocationsAnswer = answers['How many Fill locations for remote overflow alarms?'];
        if (fillLocationsAnswer === 'None') {
            return null; // Don't render anything if 'None' is selected
        }
        
        const fillLocations = parseInt(fillLocationsAnswer) || 1;
        
        if (isEditing) {
            // Parse tempAnswer to get pending changes, if any
            let pendingChanges = {};
            if (tempAnswer && tempAnswer.startsWith('{')) {
                try {
                    pendingChanges = JSON.parse(tempAnswer);
                } catch (e) {}
            }
            
            return (
                <div className="editing-controls">
                    <div className="dynamic-tank-fields">
                        {Array.from({ length: fillLocations }).map((_, index) => {
                            const answerKey = `${question} - Location ${index + 1}`;
                            const currentValue = pendingChanges[answerKey] !== undefined 
                                ? pendingChanges[answerKey] 
                                : (answers[answerKey] || '');
                            
                            return (
                                <div key={index} className="dynamic-tank-field">
                                    <label>Fill Location {index + 1}</label>
                                    <select
                                        onChange={(e) => {
                                            const newTempAnswers = { ...pendingChanges };
                                            newTempAnswers[answerKey] = e.target.value;
                                            setTempAnswer(JSON.stringify(newTempAnswers));
                                        }}
                                        value={currentValue}
                                    >
                                        <option value="">Select...</option>
                                        {getOptions().map((option, i) => (
                                            <option key={i} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                            );
                        })}
                    </div>
                    <div className="edit-buttons">
                        <button type="button" className="button-save" onClick={() => {
                            handleSave(question);
                        }}>
                            Save
                        </button>
                        <button type="button" className="button-cancel" onClick={() => setEditingQuestion(null)}>
                            Cancel
                        </button>
                    </div>
                </div>
            );
        }

        // Display per-location answers for fill locations
        const locationAnswers = Array.from({ length: fillLocations }).map((_, index) => {
            const locationAnswer = answers[`${question} - Location ${index + 1}`];
            return locationAnswer ? `Location ${index + 1}: ${locationAnswer}` : `Location ${index + 1}: N/A`;
        });

        return (
            <div className="answer-row multi-tank-answer">
                <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold' }}>{question}</div>
                    <div style={{ marginTop: '3px' }}>
                        {locationAnswers.map((answer, idx) => (
                            <div key={idx} style={{ fontSize: '0.9em', lineHeight: '1.4' }}>{answer}</div>
                        ))}
                    </div>
                </div>
                <button type="button" className="button-edit" onClick={() => {
                    setEditingQuestion(question);
                }}>
                    Edit
                </button>
            </div>
        );
    }

    if (isMultiTankQuestion) {
        const tankCount = parseInt(answers['How many Tanks/Probes, for level monitoring?']) || 1;
        
        if (isEditing) {
            // Parse tempAnswer to get pending changes, if any
            let pendingChanges = {};
            if (tempAnswer && tempAnswer.startsWith('{')) {
                try {
                    pendingChanges = JSON.parse(tempAnswer);
                } catch (e) {}
            }
            
            return (
                <div className="editing-controls">
                    <div className="dynamic-tank-fields">
                        {Array.from({ length: tankCount }).map((_, index) => {
                            const answerKey = `${question} - Tank ${index + 1}`;
                            const currentValue = pendingChanges[answerKey] !== undefined 
                                ? pendingChanges[answerKey] 
                                : (answers[answerKey] || '');
                            
                            return (
                                <div key={index} className="dynamic-tank-field">
                                    <label>Tank {index + 1}</label>
                                    <select
                                        onChange={(e) => {
                                            const newTempAnswers = { ...pendingChanges };
                                            newTempAnswers[answerKey] = e.target.value;
                                            setTempAnswer(JSON.stringify(newTempAnswers));
                                        }}
                                        value={currentValue}
                                    >
                                        <option value="">Select...</option>
                                        {getOptions().map((option, i) => (
                                            <option key={i} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                            );
                        })}
                    </div>
                    <div className="edit-buttons">
                        <button type="button" className="button-save" onClick={() => {
                            handleSave(question);
                        }}>
                            Save
                        </button>
                        <button type="button" className="button-cancel" onClick={() => setEditingQuestion(null)}>
                            Cancel
                        </button>
                    </div>
                </div>
            );
        }

        // Display per-tank answers
        const tankAnswers = Array.from({ length: tankCount }).map((_, index) => {
            const tankAnswer = answers[`${question} - Tank ${index + 1}`];
            return tankAnswer ? `Tank ${index + 1}: ${tankAnswer}` : `Tank ${index + 1}: N/A`;
        });

        return (
            <div className="answer-row multi-tank-answer">
                <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold' }}>{question}</div>
                    <div style={{ marginTop: '3px' }}>
                        {tankAnswers.map((answer, idx) => (
                            <div key={idx} style={{ fontSize: '0.9em', lineHeight: '1.4' }}>{answer}</div>
                        ))}
                    </div>
                </div>
                <button type="button" className="button-edit" onClick={() => {
                    setEditingQuestion(question);
                    // For multi-tank questions, we don't need to initialize tempAnswer
                    // as we'll update it directly when the individual tank values change
                }}>
                    Edit
                </button>
            </div>
        );
    }

    // Handle regular questions
    if (isEditing) {
        return (
            <div className="editing-controls">
                <select 
                    onChange={(e) => setTempAnswer(e.target.value)}
                    value={tempAnswer || answers[question] || ''}
                >
                    <option value="">Select...</option>
                    {getOptions().map((option, index) => (
                        <option 
                            key={index} 
                            value={typeof option === 'object' ? option.value : option}
                            disabled={typeof option === 'object' ? option.disabled : false}
                        >
                            {typeof option === 'object' ? option.value : option}
                        </option>
                    ))}
                </select>
                <div className="edit-buttons">
                    <button type="button" className="button-save" onClick={() => handleSave(question)}>
                        Save
                    </button>
                    <button type="button" className="button-cancel" onClick={() => setEditingQuestion(null)}>
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="answer-row">
            <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold' }}>{question}</div>
                <div style={{ fontSize: '0.9em', lineHeight: '1.4', marginTop: '3px' }}>{answers[question] || 'N/A'}</div>
            </div>
            <button type="button" className="button-edit" onClick={() => {
                setEditingQuestion(question);
                setTempAnswer(answers[question] || '');
            }}>
                Edit
            </button>
        </div>
    );
  };

  function getProteusImage(recommendation) {
    // Extract the base model from the recommendation string
    const modelMatch = recommendation.match(/Proteus (OEL8000III[KX][\w-]+)/);
    if (!modelMatch) return null;
    
    const model = modelMatch[1];
    
    // Check for SS models first
    if (model.includes('-SS')) 
    {
        if (model.includes('K4')) 
        {
            return "/proteus_k_ss.png";
        } 
        else if (model.includes('K8')) 
        {
            return "/proteus_k_ss.png";
        }
        return "/proteus_k_ss.png"; // fallback for other SS models
    }
    // Then check for other models
    else if (model.includes('K')) 
    {
        return "/proteus_k.png";
    } 
    else if (model.includes('X') && !model.includes('SS')) 
    {
        return "/proteus_x.png";
    }
    
    return null;
  }

  const validityStyle = {
    color: validityInfo.isExpired ? '#ff0000' : '#008000',
    fontWeight: 'bold'
  };

  return (
    <div className="recommendation">
      {/* Add console log for debugging */}
      {console.log("Rendering with slotExceeded:", slotExceeded)}
      
      <div className='recommendSumH'>
        <h1>Recommendation Summary</h1>
        <h2 className="recommended-model">Proteus Model:</h2>
        <h3>{model}</h3>
        <div className="proteus-image">
          <img 
              src={getProteusImage(recommendation)} 
              alt="Proteus Model" 
              style={{ maxWidth: '100%', height: 'auto', width: '100%', maxWidth: '400px' }}
          />
        </div>
        <h3>Quote Number: {currentQuoteNumber}</h3>
        <div style={validityStyle}>
          {validityInfo.isExpired 
            ? `This quote has expired on: ${validityInfo.date}`
            : `Quote Valid Until: ${validityInfo.date}`
          }
        </div>
      </div>

      <div className='Omntec-info'>
        <p>2420 Pond Road</p>
        <p>Ronkonkoma, NY 11779</p>
        <p>Phone: 631-981-2001</p>
        <p>Fax: 631-981-2007</p>
      </div>


      <table className="customerPrintableInfo">
      <thead>
      <tr>
            <th>User/Facility: </th>
            <th>Project Name: </th>
            <th>Quote Number: </th>
            <th>Phone Number: </th>

          </tr>
      </thead>
      <tbody>
        <tr>
          <td>{userInfo.name} / {userInfo.company}</td>
          <td>{userInfo.projectName || 'N/A'}</td>
          <td>{currentQuoteNumber}</td>
          <td>{userInfo.phoneNumber}</td>
        </tr>
      </tbody>
      </table>

      <div className="discount-controls">
        <label htmlFor="discount">Distributor Discount (%): </label>
        <input
            type="number"
            id="discount"
            min="0"
            max="25"
            value={userDiscount}
            onChange={async (e) => {
                const newDiscount = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                try {
                    const response = await axios.put(`${BASE_URL}/api/users/${user.id}/discount`, {
                        discount: newDiscount
                    });
                    if (response.data.success) {
                        setUserDiscount(newDiscount);
                    }
                } catch (error) {
                    console.error('Error saving user discount:', error);
                }
            }}
            className="discount-input"
        />
      </div>

      {missingSensorQuestions.length > 0 && (
        <div className="missing-questions-warning" style={{
          backgroundColor: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: '8px',
          padding: '15px 20px',
          marginBottom: '20px',
          color: '#856404'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#856404', display: 'flex', alignItems: 'center', gap: '8px' }}>
            ⚠️ Additional Information Required for Leak Sensors
          </h4>
          <p style={{ margin: '0 0 10px 0' }}>
            You've configured leak sensors, but the following questions need to be answered in the <strong>Site Configuration</strong> section below for the sensors to appear correctly in your quote:
          </p>
          <ul style={{ margin: '0', paddingLeft: '20px' }}>
            {missingSensorQuestions.map((q, idx) => (
              <li key={idx} style={{ marginBottom: '4px' }}>{q}</li>
            ))}
          </ul>
          <p style={{ margin: '10px 0 0 0', fontStyle: 'italic' }}>
            Expand "Site Configuration" below and click "Edit" on each question to provide the required information.
          </p>
        </div>
      )}

      <h3>Configuration Summary:</h3>
      <table className="configuration-table">
        <thead>
          <tr>
            <th>Configuration/Part</th>
            <th>Quantity</th>
            <th>Unit Price</th>
            <th>Total Price</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={index} className={item.isWarning ? "warning-item" : ""}>
              <td style={item.isWarning ? {color: '#cc0000', fontWeight: 'bold'} : {}}>{item.name}</td>
              <td>{item.quantity}</td>
              <td>${item.price.toFixed(2)}</td>
              <td>${(item.totalPrice || (item.price * item.quantity)).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          {(slotExceeded || (items && items.some(item => 
            (item.name.includes('XB-800') || item.name.includes('XB-416')) && 
            items.reduce((total, i) => 
              total + (i.name.includes('XB-800') || i.name.includes('XB-416') ? i.quantity : 0), 0) > 5
          ))) && (
            <tr className="slot-exceeded-warning">
              <td colSpan="4" style={{ 
                backgroundColor: '#ffff99', 
                color: '#cc0000', 
                fontWeight: 'bold', 
                padding: '15px', 
                textAlign: 'center',
                fontSize: '1.1em',
                border: '2px solid #cc0000'
              }}>
                DISCLAIMER: You have exceeded the capacity of input boards available (6 boards maximum). Contact Omntec directly for assistance in providing a solution for this application.
              </td>
            </tr>
          )}
          <tr>
            <td colSpan="3"><strong>Total List Price</strong></td>
            <td><strong>${totalPrice.toFixed(2)}</strong></td>
          </tr>
          <tr>
            <td colSpan="3"><strong>Distributor Discount ({userDiscount}%)</strong></td>
            <td><strong>-${((totalPrice * userDiscount) / 100).toFixed(2)}</strong></td>
          </tr>
          <tr className="grand-total">
            <td colSpan="3"><strong>Grand Total</strong></td>
            <td><strong>${(totalPrice - (totalPrice * userDiscount) / 100).toFixed(2)}</strong></td>
          </tr>
        </tfoot>
      </table>
      <div className="disclaimer" style={{display: 'inline-block'}}>
          <h3>IMPORTANT!</h3>
          <p>All prices, product availability, and shipping estimates are subject to change without prior notice due to market fluctuations and supply chain conditions. </p>
          <p>Final pricing and lead times will be confirmed at the time of order placement. Additional taxes, duties, or fees may apply and will be included in the final invoice.</p>
      </div>

      <div className="recommendation-buttons">
        <button className='button-print' onClick={handlePrint}>Print Quote</button>
        <button className='button-new-quote' onClick={resetQuoteGenerator}>New Quote</button>
        <button className='button-back-to-quotes' onClick={backToQuotesList}>Back to Quotes List</button>
      </div>

      <div className="user-info">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 15px' }}>
          <h2 style={{ padding: '10px 0' }}>Facility Information</h2>
          {!isEditingFacilityInfo ? (
            <button 
              className="button-edit" 
              onClick={handleEditFacilityInfo}
              style={{ fontSize: '14px', padding: '5px 15px' }}
            >
              Edit
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="button-save" 
                onClick={handleSaveFacilityInfo}
                style={{ fontSize: '14px', padding: '5px 15px', backgroundColor: '#28a745' }}
              >
                Save
              </button>
              <button 
                className="button-cancel" 
                onClick={handleCancelEditFacilityInfo}
                style={{ fontSize: '14px', padding: '5px 15px', backgroundColor: '#dc3545' }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        
        {!isEditingFacilityInfo ? (
          <>
            <p>Email: {userInfo.email}</p>
            <p>Name: {userInfo.name}</p>
            <p>Company: {userInfo.company}</p>
            <p>Project Name: {userInfo.projectName}</p>
            <p>City: {userInfo.city}</p>
            <p>State: {userInfo.state}</p>
            <p>Phone Number: {userInfo.phoneNumber}</p>
          </>
        ) : (
          <div style={{ marginTop: '15px', padding: '0 15px' }}>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Email:</label>
              <input
                type="email"
                value={editedFacilityInfo.email}
                onChange={(e) => handleFacilityInfoChange('email', e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Name:</label>
              <input
                type="text"
                value={editedFacilityInfo.name}
                onChange={(e) => handleFacilityInfoChange('name', e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Company:</label>
              <input
                type="text"
                value={editedFacilityInfo.company}
                onChange={(e) => handleFacilityInfoChange('company', e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Project Name:</label>
              <input
                type="text"
                value={editedFacilityInfo.projectName}
                onChange={(e) => handleFacilityInfoChange('projectName', e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>City:</label>
              <input
                type="text"
                value={editedFacilityInfo.city}
                onChange={(e) => handleFacilityInfoChange('city', e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>State:</label>
              <input
                type="text"
                value={editedFacilityInfo.state}
                onChange={(e) => handleFacilityInfoChange('state', e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Phone Number:</label>
              <input
                type="tel"
                value={editedFacilityInfo.phoneNumber}
                onChange={(e) => handleFacilityInfoChange('phoneNumber', e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
          </div>
        )}
      </div>
      

      <div className="questions-answers"> 
        <h2 onClick={toggleSiteConfig} style={{ cursor: 'pointer' }}>
          Site Configuration {isSiteConfigExpanded ? '▼' : '▶'}
        </h2>
        {isSiteConfigExpanded && (
          <div className="site-config-details">
            <p className="site-config-instructions">
              Click "Edit" next to any question to modify your answers. Changes will update the quote automatically.
            </p>
            <h3>Initial Questions</h3>
            <div className="question-section">
              {QuestionsModule.initialQuestions.map((question, index) => 
                renderQuestionAnswer(question.question)
              )}
            </div>
            <h3>Configuration Questions</h3>
            <div className="question-section">
              {QuestionsModule.questions.map((question, index) => 
                renderQuestionAnswer(question.question)
              )}
            </div>
          </div>
        )}
      </div>
        <div className="printable-disclaimer">
          <h3>IMPORTANT!</h3>
          <p>Please reference quote number when ordering.</p>
          <p>Custom parts are non cancellable and non returnable</p>
        </div>

    </div>
  );
}

export default Recommendation;
